import { FormGroupStatus } from 'src/core/constants/form/form-group-status.constant';
import { ValidationMessageService } from 'src/core/services/validation/validation-message.service';
import { FormBuilder } from '@angular/forms';
import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { FilterPanelEditorConfigurationModel } from 'src/ca-shared/filter-panel/models/filter-panel-editor-configuration.model';
import { FilterPanelEditorComponent } from 'src/ca-shared/filter-panel/models/filter-panel-editor.component';
import { CrudService } from 'src/core/services/crud/crud.service';
import { ListRequestDto } from 'src/core/models/request/list-request.dto';
import { ListResponseDto } from 'src/core/models/request/list-response.dto';
import { EntitySelectionConfigurationModel } from '../models/entity-selection-configuration.model';
import { EntitySelectionType } from '../models/entity-selection-type.enum';

const DEFAULT_MAX_RESULT_COUNT = 2147483647;

@Component({
  selector: 'ca-entity-selection-panel',
  templateUrl: './entity-selection-panel.component.html',
  styleUrls: ['./entity-selection-panel.component.scss'],
  standalone: false,
})
export class EntitySelectionPanelComponent
  extends FilterPanelEditorComponent
  implements OnInit, OnDestroy
{
  items: { id: number | string; name: string }[] = [];
  selectionType = EntitySelectionType;
  errorMessage: string[] = [];
  randomKey: string;
  actualConfig: EntitySelectionConfigurationModel;

  loading = false;
  loadError = false;

  _value: any;

  private readonly destroy$ = new Subject<void>();

  @Input() set config(value: FilterPanelEditorConfigurationModel) {
    this.actualConfig = value as EntitySelectionConfigurationModel;
  }

  get config(): FilterPanelEditorConfigurationModel {
    return this.actualConfig;
  }

  get value(): any {
    if (
      this._value &&
      this._value.length &&
      this.actualConfig &&
      this.actualConfig.valuesToExclude
    ) {
      this._value = this._value.filter(v => !this.actualConfig.valuesToExclude.includes(v));
    }
    return this._value;
  }

  set value(value: any) {
    if (value && value.length && this.actualConfig && this.actualConfig.valuesToExclude) {
      value = value.filter(v => !this.actualConfig.valuesToExclude.includes(v));
    }
    this._value = value;

    this.editorForm.patchValue({
      editor: this.value,
    });
  }

  get labelClass(): string {
    let cls = '';
    if (this.actualConfig.inlineCls) {
      cls = this.actualConfig.inlineCls;
    }
    return cls;
  }

  get hasItems(): boolean {
    return this.items.length > 0;
  }

  init(): void {
    super.init();
    this.loadEntityItems();
  }

  loadEntityItems(): void {
    const valueField = (this.actualConfig.valueField as string) || 'id';
    const labelField = (this.actualConfig.labelField as string) || 'name';

    const request: ListRequestDto = {
      skipCount: 0,
      maxResultCount: this.actualConfig.maxResultCount ?? DEFAULT_MAX_RESULT_COUNT,
      filters: this.actualConfig.filters ?? [],
      sorters: this.actualConfig.sorters ?? [],
    };

    this.loading = true;
    this.loadError = false;

    this.crudService
      .get<any>(this.actualConfig.entityType, request)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response: ListResponseDto<any>) => {
          let list = (response?.items ?? []).map(item => ({
            id: item[valueField],
            name: item[labelField],
          }));

          if (this.actualConfig.valuesToExclude) {
            list = list.filter(item => !this.actualConfig.valuesToExclude.includes(item.id));
          }

          this.items = list;
          this.loading = false;
        },
        error: () => {
          this.items = [];
          this.loading = false;
          this.loadError = true;
        },
      });
  }

  getLabel(item: { id: number | string; name: string }): string {
    if (this.actualConfig.labelLocalizationPrefix) {
      return this.actualConfig.labelLocalizationPrefix + ':' + item.name;
    }
    return item.name;
  }

  isVisible(item: { id: number | string; name: string }): boolean {
    if (!this.actualConfig.valuesToExclude || this.actualConfig.valuesToExclude.length === 0) {
      return true;
    }
    return !this.actualConfig.valuesToExclude.includes(item.id);
  }

  trackByItemId(index: number, item: { id: number | string; name: string }): number | string {
    return item.id;
  }

  trackByMessage(index: number, message: string): string {
    return message;
  }

  reload(): void {}

  reset(): void {}

  autoSelectFirstItem(): void {}

  onCheckChanged(eventArgs): void {
    if (
      this.actualConfig.type == this.selectionType.Combobox &&
      this.actualConfig.multiple === true
    ) {
      this.value = [];
      if (eventArgs.length > 0) {
        this.value = eventArgs;
      }
    } else if (this.actualConfig.type == this.selectionType.Radio) {
      const val = eventArgs.currentTarget.value;

      this.value = [val];
    } else {
      const val = eventArgs.currentTarget.value;
      const checked = eventArgs.currentTarget.checked;
      const idx = this.value.indexOf(val);

      if (checked) {
        if (idx < 0) {
          this.value.push(val);
        }
      } else {
        if (idx > -1) {
          this.value.splice(idx, 1);
        }
      }
    }

    this.editorForm.patchValue({
      editor: this.value,
    });

    this.onChange();
  }

  onSelectChange(eventArgs): void {
    const val = eventArgs;

    if (this.value !== val) {
      this.value = val;

      this.onChange();
    }
  }

  constructor(
    protected fb: FormBuilder,
    private validationMessageService: ValidationMessageService,
    private crudService: CrudService,
  ) {
    super(fb);
    this.editorForm.statusChanges.pipe(takeUntil(this.destroy$)).subscribe(x => {
      if (x === FormGroupStatus.INVALID) {
        for (const error in this.editorForm.controls.editor.errors) {
          if (this.editorForm.controls.editor.errors.hasOwnProperty(error)) {
            const errorMessage = this.validationMessageService[error];
            this.errorMessage.push(errorMessage);
          }
        }
      } else {
        this.errorMessage = [];
      }
    });

    this.randomKey = new Date().valueOf().toString();
  }

  ngOnInit(): void {}

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
